import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Check, Clock, CreditCard, Info, Lightbulb, MapPin, Phone, Star, Utensils, X } from 'lucide-react';
import SEO from '../SEO';
import { SitePage, PageHero, CtaBand } from './ui';
import { BRAND, inr, toNumber, waLink } from './brand';
import { getDestination } from './destinations';
import { submitEnquiry } from './api';
import './package.css';
import { packageImage } from './packageImages';
import { isInternational, taxLabel, withTaxes } from './taxes';
import usePackageContent from './usePackageContent';
import PackageDetails, { routeText } from './PackageDetails';
import HoneyPot from './HoneyPot';
import PeakNote from './PeakNote';

const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []).filter((x) => x !== null && x !== undefined && String(x).trim() !== '');

const EMPTY = { name: '', email: '', phone: '', date: '', adults: 2, children: 0, message: '', pan: '' };
const today = () => new Date().toISOString().split('T')[0];

/** Enquiry form shown in the sidebar (and reused on small screens). */
const EnquiryForm = ({ pkg, destinationName }) => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ busy: false, ok: false, err: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, ok: false, err: '' });
    try {
      await submitEnquiry({
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), message: f.message,
        packageId: pkg.id, packageTitle: pkg.title, destination: destinationName,
        travelDate: f.date, travelers: Number(f.adults) + Number(f.children),
      });
      setState({ busy: false, ok: true, err: '' });
      setF(EMPTY);
    } catch {
      setState({ busy: false, ok: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.` });
    }
  };

  if (state.ok) {
    return (
      <div className="vk-alert vk-alert--ok" role="status">
        Thank you! Your enquiry for <b>{pkg.title}</b> has reached our team. We'll call you within working hours.
      </div>
    );
  }

  return (
    <form className="vk-form" onSubmit={submit} aria-label="Enquiry form"><HoneyPot />
      <div className="vk-field"><label htmlFor="enq-name">Full name *</label><input id="enq-name" required value={f.name} onChange={set('name')} autoComplete="name" /></div>
      <div className="vk-form__row">
        <div className="vk-field"><label htmlFor="enq-phone">Phone *</label><input id="enq-phone" required type="tel" inputMode="tel" pattern="[0-9+\s()-]{10,16}" title="10-digit mobile number" placeholder="10-digit mobile" value={f.phone} onChange={set('phone')} autoComplete="tel" /></div>
        <div className="vk-field"><label htmlFor="enq-email">Email <small style={{ fontWeight: 400 }}>(optional)</small></label><input id="enq-email" type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
      </div>
      <div className="vk-form__row">
        <div className="vk-field"><label htmlFor="enq-date">Travel date</label><input id="enq-date" type="date" min={today()} value={f.date} onChange={set('date')} /></div>
        <div className="vk-field"><label htmlFor="enq-adults">Travellers</label><input id="enq-adults" type="number" min="1" max="50" value={f.adults} onChange={set('adults')} /></div>
      </div>
      <div className="vk-field"><label htmlFor="enq-msg">Anything we should know?</label><textarea id="enq-msg" rows={3} value={f.message} onChange={set('message')} placeholder="Hotel preference, flights from your city, special occasions…" /></div>
      {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
      <button type="submit" className="vk-btn vk-btn--dark vk-btn--block" disabled={state.busy}>{state.busy ? 'Sending…' : 'Send enquiry'}</button>
      <PeakNote compact />
      <p className="vk-form__note">No payment needed. We reply with a confirmed quote.</p>
    </form>
  );
};

/** Booking request modal: no online payment — the request lands in the CRM and our team confirms. */
const BookingModal = ({ pkg, price, onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ busy: false, err: '', done: null });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const adults = Math.max(1, Number(f.adults) || 1);
  const children = Math.max(0, Number(f.children) || 0);
  const intl = isInternational(pkg);
  const t = withTaxes(price * adults + price * 0.5 * children, intl);
  const total = t.total;

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, err: '', done: null });
    try {
      const taxNote = `Estimate: base ${t.base} + GST ${t.gst}${intl ? ` + TCS ${t.tcs} (PAN ${f.pan.trim().toUpperCase()})` : ''} = ${total}`;
      await submitEnquiry({
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(),
        packageId: pkg.id, packageTitle: pkg.title, destination: pkg.destination,
        travelDate: f.date, travelers: adults + children,
        message: [`BOOKING REQUEST — ${adults} adult(s), ${children} child(ren)`, taxNote, f.message].filter(Boolean).join('\n'),
      });
      setState({ busy: false, err: '', done: true });
    } catch (err) {
      setState({ busy: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.`, done: null });
    }
  };

  return (
    <div className="vk-modal" role="dialog" aria-modal="true" aria-labelledby="book-title" onClick={(e) => e.target === e.currentTarget && !state.busy && onClose()}>
      <div className="vk-modal__box">
        <button type="button" className="vk-modal__x" aria-label="Close" onClick={onClose} disabled={state.busy}><X size={22} /></button>
        {state.done ? (
          <div>
            <p className="vk-kicker">Request received</p>
            <h3 id="book-title" className="vk-display" style={{ fontSize: 36, margin: '0 0 12px' }}>Thank you!</h3>
            <p>Your booking request for <b>{pkg.title}</b> has reached our team. We'll call you within working hours to confirm availability, hotels and payment details.</p>
            <button type="button" className="vk-btn vk-btn--dark vk-btn--block" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className="vk-form" onSubmit={submit}><HoneyPot />
            <div>
              <p className="vk-kicker" style={{ marginBottom: 4 }}>Request a booking</p>
              <h3 id="book-title" style={{ margin: 0, fontSize: 20 }}>{pkg.title}</h3>
              <p className="vk-form__note">{pkg.duration} · {inr(price)} per adult · children 50%</p>
            </div>
            <div className="vk-field"><label htmlFor="bk-name">Full name *</label><input id="bk-name" required value={f.name} onChange={set('name')} autoComplete="name" /></div>
            <div className="vk-form__row">
              <div className="vk-field"><label htmlFor="bk-phone">Phone *</label><input id="bk-phone" required type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></div>
              <div className="vk-field"><label htmlFor="bk-email">Email *</label><input id="bk-email" required type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
            </div>
            <div className="vk-field"><label htmlFor="bk-date">Travel date *</label><input id="bk-date" required type="date" min={today()} value={f.date} onChange={set('date')} /></div>
            <div className="vk-form__row">
              <div className="vk-field"><label htmlFor="bk-ad">Adults</label><input id="bk-ad" type="number" min="1" max="20" value={f.adults} onChange={set('adults')} /></div>
              <div className="vk-field"><label htmlFor="bk-ch">Children (2–11)</label><input id="bk-ch" type="number" min="0" max="10" value={f.children} onChange={set('children')} /></div>
            </div>
            {intl ? <div className="vk-field"><label htmlFor="bk-pan">PAN of lead traveller * <small style={{ fontWeight: 400 }}>(required for TCS on overseas tours)</small></label><input id="bk-pan" required value={f.pan} onChange={set('pan')} pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]" maxLength={10} style={{ textTransform: 'uppercase' }} placeholder="ABCDE1234F" /></div> : null}
            <div className="vk-field"><label htmlFor="bk-msg">Special requests</label><textarea id="bk-msg" rows={2} value={f.message} onChange={set('message')} /></div>
            <div className="vk-total">
              <div><span>Adults {adults} × {inr(price)}</span><span>{inr(price * adults)}</span></div>
              {children > 0 ? <div><span>Children {children} × {inr(price * 0.5)}</span><span>{inr(price * 0.5 * children)}</span></div> : null}
              <div><span>GST (5%)</span><span>{inr(t.gst)}</span></div>
              {intl ? <div><span>TCS (2%) — claimable in your ITR</span><span>{inr(t.tcs)}</span></div> : null}
              <div className="vk-total__sum"><span>Estimated total</span><span>{inr(total)}</span></div>
            </div>
            <PeakNote compact />
            {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
            <button type="submit" className="vk-btn vk-btn--hot vk-btn--block" disabled={state.busy}><CalendarCheck size={18} /> {state.busy ? 'Sending…' : 'Send booking request'}</button>
            <p className="vk-form__note" style={{ textAlign: 'center' }}>No payment now. We confirm availability first, then share payment details.</p>
          </form>
        )}
      </div>
    </div>
  );
};

/**
 * Full package page. `pkg` is the package object from the API; `destinationId` the
 * destination key (e.g. "kerala").
 */
const PackageView = ({ pkg, destinationId, canonicalPath }) => {
  const [tab, setTab] = useState('overview');
  const [booking, setBooking] = useState(false);
  const [failed, setFailed] = useState([]);
  const { content: c } = usePackageContent(pkg.id);
  const dest = useMemo(() => getDestination(destinationId || pkg.destination), [destinationId, pkg.destination]);
  const destName = dest?.name || (pkg.destination ? pkg.destination.charAt(0).toUpperCase() + pkg.destination.slice(1) : '');

  const price = toNumber(pkg.price);
  const original = toNumber(pkg.originalPrice);
  const save = price && original && original > price ? original - price : null;
  const itinerary = asList(pkg.itinerary).filter((d) => d && (d.title || d.description));
  const inclusions = asList(pkg.inclusions);
  const exclusions = asList(pkg.exclusions);
  const tips = asList(pkg.travelTips);
  const info = pkg.importantInfo && typeof pkg.importantInfo === 'object' ? pkg.importantInfo : {};
  const places = asList(pkg.highlights).slice(0, 8);
  const gallery = asList(pkg.gallery);
  const msg = `Hi VK Tours, I'm interested in "${pkg.title}" (${pkg.duration}). Please share availability.`;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    itinerary.length ? { key: 'itinerary', label: `Itinerary (${itinerary.length} days)` } : null,
    inclusions.length || exclusions.length ? { key: 'inclusions', label: 'Inclusions' } : null,
    Object.values(info).some(Boolean) || tips.length ? { key: 'info', label: 'Good to know' } : null,
  ].filter(Boolean);

  return (
    <SitePage>
      <SEO
        title={`${pkg.title}${pkg.duration ? ` · ${pkg.duration}` : ''}`}
        description={c ? `${pkg.title} — ${pkg.duration}. ${c.summary}`.slice(0, 180) : `${pkg.title} — ${pkg.duration || ''}. ${(pkg.description || '').slice(0, 140)}`}
        image={packageImage(pkg, 1200)}
        path={canonicalPath}
        type="product"
        jsonLd={[{
          '@context': 'https://schema.org',
          '@type': 'TouristTrip',
          name: pkg.title,
          description: (c ? c.summary : pkg.description || '').slice(0, 300),
          itinerary: c && Array.isArray(c.days) ? { '@type': 'ItemList', itemListElement: c.days.map((d, i) => ({ '@type': 'ListItem', position: i + 1, name: d.title })) } : undefined,
          image: packageImage(pkg, 1200),
          offers: price ? { '@type': 'Offer', price, priceCurrency: 'INR', availability: 'https://schema.org/InStock', priceSpecification: { '@type': 'UnitPriceSpecification', price, priceCurrency: 'INR', valueAddedTaxIncluded: false } } : undefined,
          provider: { '@type': 'TravelAgency', name: 'VK Tours', url: 'https://vktours.com' },
        }, c && Array.isArray(c.faqs) && c.faqs.length ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: c.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        } : null].filter(Boolean)}
      />

      <PageHero
        short
        className="vk-phero--pkg"
        image={packageImage(pkg, 2000)}
        fallbackImage={dest?.heroImage}
        imageAlt={pkg.title}
        crumbs={[{ label: 'Home', to: '/' }, { label: destName || 'Packages', to: dest ? dest.path : '/tour-packages' }, { label: pkg.title }]}
        kicker={[pkg.duration, destName].filter(Boolean).join(' · ')}
        title={pkg.title}
        lede={c ? c.tagline : undefined}
      >
        <a className="vk-badge vk-badge--sun" href={BRAND.googleReviews} target="_blank" rel="noopener noreferrer"><Star size={14} /> {BRAND.googleRating} on Google · VK Tours</a>
              </PageHero>

      <section className="vk-section" style={{ paddingTop: 36 }}>
        <div className="vk-wrap vk-pkg">
          <div className="vk-pkg__main">
            {price ? (
              <div className="vk-pkg__mprice">
                <div>
                  <strong>{inr(price)}</strong>
                  <small>per person on twin sharing {taxLabel(isInternational(pkg))}</small>
                </div>
                <a className="vk-btn vk-btn--hot" href="#book">Book or enquire</a>
              </div>
            ) : null}
            {price ? <PeakNote /> : null}
            {c ? <PackageDetails c={c} pkg={pkg} /> : (<>
            {places.length > 0 && (
              <ul className="vk-chips" style={{ marginBottom: 20 }}>
                {places.map((p) => <li key={p} className="vk-chip"><MapPin size={14} /> {p}</li>)}
              </ul>
            )}

            <div className="vk-tabs" role="tablist" aria-label="Package details">
              {tabs.map((t) => (
                <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`vk-tab ${tab === t.key ? 'is-active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
              ))}
            </div>

            <div className="vk-tabpanel" role="tabpanel">
              {tab === 'overview' && (
                <div className="vk-prose">
                  {String(pkg.description || '').split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
                  {gallery.filter((g) => !failed.includes(g)).length >= 3 && (
                    <div className="vk-gallery" style={{ marginTop: 24 }}>
                      {gallery.filter((g) => !failed.includes(g)).slice(0, 5).map((g, i) => <img key={g} src={g} alt={`${pkg.title} photo ${i + 1}`} loading="lazy" onError={() => setFailed((f) => [...f, g])} />)}
                    </div>
                  )}
                  {itinerary.length > 0 && (
                    <>
                      <h3>Trip at a glance</h3>
                      <ul>{itinerary.map((d) => <li key={d.day}><b>Day {d.day}:</b> {d.title}</li>)}</ul>
                    </>
                  )}
                </div>
              )}

              {tab === 'itinerary' && (
                <ol className="vk-days">
                  {itinerary.map((d) => (
                    <li key={d.day} className="vk-day">
                      <span className="vk-day__num">{d.day}</span>
                      <div>
                        <h4>{d.title}</h4>
                        {d.description ? <p>{d.description}</p> : null}
                        {asList(d.activities).length ? <ul className="vk-chips" style={{ marginTop: 10 }}>{asList(d.activities).map((a) => <li key={a} className="vk-chip" style={{ fontSize: 13, padding: '6px 10px' }}>{a}</li>)}</ul> : null}
                        {d.meals ? <p className="vk-form__note" style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}><Utensils size={14} /> Meals: {d.meals}</p> : null}
                      </div>
                    </li>
                  ))}
                </ol>
              )}

              {tab === 'inclusions' && (
                <div className="vk-pkg__inc">
                  <div className="vk-panel">
                    <h3>Included</h3>
                    <ul className="vk-ticks vk-ticks--yes">{inclusions.map((x) => <li key={x}><Check size={18} /> {x}</li>)}</ul>
                  </div>
                  <div className="vk-panel">
                    <h3>Not included</h3>
                    <ul className="vk-ticks vk-ticks--no">{exclusions.map((x) => <li key={x}><X size={18} /> {x}</li>)}</ul>
                  </div>
                </div>
              )}

              {tab === 'info' && (
                <div style={{ display: 'grid', gap: 16 }}>
                  {Object.values(info).some(Boolean) && (
                    <div className="vk-infogrid">
                      {info.best_time ? <div className="vk-info"><Clock size={20} /><div><b>Best time</b><span>{info.best_time}</span></div></div> : null}
                      {info.visa ? <div className="vk-info"><Info size={20} /><div><b>Visa</b><span>{info.visa}</span></div></div> : null}
                      {info.currency ? <div className="vk-info"><CreditCard size={20} /><div><b>Currency</b><span>{info.currency}</span></div></div> : null}
                      {info.language ? <div className="vk-info"><Info size={20} /><div><b>Language</b><span>{info.language}</span></div></div> : null}
                    </div>
                  )}
                  {tips.length > 0 && (
                    <div className="vk-panel vk-panel--sand">
                      <h3>Travel tips</h3>
                      <ul className="vk-ticks">{tips.map((t) => <li key={t}><Lightbulb size={18} color="var(--hot)" /> {t}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>)}
          </div>

          <aside className="vk-pkg__aside">
            <div className="vk-aside">
              <div className="vk-panel" id="book" style={{ scrollMarginTop: 'calc(var(--header-h) + 16px)' }}>
                <div className="vk-price">
                                    <strong>{price ? inr(price) : 'On request'}</strong>
                  {price ? <small>per person on twin sharing · {pkg.duration}</small> : null}
                  {price ? <small className="vk-price__tax">{taxLabel(isInternational(pkg))} extra (government taxes)</small> : null}
                </div>
                {price ? <PeakNote compact /> : null}
                {c && Array.isArray(c.route) && c.route.length ? <p className="vk-pd__asideroute"><MapPin size={15} /> {routeText(c.route)}</p> : null}
                <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
                  {price ? <button type="button" className="vk-btn vk-btn--hot vk-btn--block" onClick={() => setBooking(true)}><CalendarCheck size={18} /> Book this trip</button> : null}
                  <a className="vk-btn vk-btn--whatsapp vk-btn--block" href={waLink(msg)} target="_blank" rel="noopener noreferrer">Ask on WhatsApp</a>
                  <a className="vk-btn vk-btn--outline vk-btn--block" href={BRAND.phoneLink}><Phone size={18} /> {BRAND.phone}</a>
                </div>
              </div>
              <div className="vk-panel" id="enquire">
                <h3>Send an enquiry</h3>
                <EnquiryForm pkg={pkg} destinationName={destName} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {dest ? (
        <section className="vk-section vk-section--sand" style={{ paddingTop: 48, paddingBottom: 48 }}>
          <div className="vk-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <p className="vk-display" style={{ fontSize: 'clamp(26px,3.5vw,40px)', margin: 0 }}>More {destName} packages</p>
            <Link className="vk-btn vk-btn--dark" to={dest.path}>See all {destName} trips</Link>
          </div>
        </section>
      ) : null}

      <CtaBand message={msg} />
      {booking && price ? <BookingModal pkg={pkg} price={price} onClose={() => setBooking(false)} /> : null}
    </SitePage>
  );
};

export default PackageView;
